import { apiBaseUrl, TOKEN_STORAGE_KEY } from '../auth/config';

export type ApiError = {
  status: number;
  code?: string;
  message: string;
  /** The raw parsed JSON error body (when the server sent an object), for
   *  callers that need structured fields beyond `code`/`message` — e.g. the
   *  console-session 409's `heldByDisplayName`/`startedAt`/`activeSessionId`.
   *  Most callers never touch this; it's additive and never required. */
  details?: Record<string, unknown>;
};

/**
 * Tiny fetch wrapper.
 *
 * - Same-origin in dev (Vite proxies /api/* to the API on :5200).
 * - Adds the bearer token if `getToken` resolves to one.
 * - Throws an ApiError-shaped object on non-2xx so React Query surfaces
 *   the server's `code` + `message` to the UI.
 *
 * <para>
 * Global 401 handling: the token is cleared and the browser is sent to
 * <c>/login</c> before the error is thrown. Catches every "session
 * expired mid-page" case across all queries / mutations without each
 * page handling it individually. The exemption is unauthenticated
 * endpoints (the bootstrap-status check and the login flow itself) —
 * those are reached without a token, so a 401 there is the server
 * legitimately reporting "not authenticated", not "your session
 * expired."
 * </para>
 */
export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  getToken?: () => Promise<string | null>,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  let hadToken = false;
  if (getToken) {
    const token = await getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      hadToken = true;
    }
  }

  const response = await fetch(`${apiBaseUrl}${path}`, { ...init, headers });
  const text = await response.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    // Session expired mid-page: clear the token and bounce to login.
    // We only treat this as "session expired" when the request actually
    // carried a token — otherwise a 401 from /me on an anonymous page
    // would loop us through /login → / → /login.
    if (response.status === 401 && hadToken && typeof window !== 'undefined') {
      try { window.sessionStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* sessionStorage may be blocked */ }
      // Use replace so the back button doesn't bounce the user back into
      // the page that just 401'd. Skip if we're already on /login.
      // `?reason=expired` lets LoginPage show a one-line notice so the
      // user understands why they landed back on the sign-in screen.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.replace('/login?reason=expired');
      }
    }
    const obj = (parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {});
    const serverMsg = typeof obj.message === 'string' ? obj.message : '';
    const code      = typeof obj.code    === 'string' ? obj.code    : undefined;
    // Always tell the caller (and ultimately the user) which method +
    // path failed and what the server said. "Not Found" alone is not
    // useful — they need to know *what* wasn't found.
    const method   = (init.method ?? 'GET').toUpperCase();
    const status   = `HTTP ${response.status}${response.statusText ? ' ' + response.statusText : ''}`;
    const detail   = serverMsg || (typeof parsed === 'string' && parsed.length > 0 ? parsed as string : '');
    const tail     = detail ? ` — ${detail}` : '';
    const err: ApiError = {
      status: response.status,
      code,
      message: `${method} ${path}: ${status}${tail}`,
      details: obj,
    };
    throw err;
  }

  return parsed as T;
}

/**
 * Blob variant of {@link apiFetch} for binary endpoints (e.g. the operator
 * logo at GET /api/v1/operators/{id}/logo).
 *
 * An HTML `<img src>` can't carry the `Authorization` header, so a plain
 * `<img>` against a `.RequireAuthorization()` endpoint 401s every time. This
 * fetches the bytes WITH the bearer token, mirroring apiFetch's auth handling
 * and the global "session expired → bounce to /login" behaviour, and returns
 * the raw {@link Blob} so the caller can wrap it in an object-URL and feed
 * THAT to `<img src>`.
 *
 * Differences from apiFetch: no JSON `Content-Type`; `Accept` asks for any
 * image; the body is returned via `response.blob()` rather than JSON-parsed.
 */
export async function apiFetchBlob(
  path: string,
  getToken?: () => Promise<string | null>,
  init: RequestInit = {},
): Promise<Blob> {
  const headers = new Headers(init.headers);
  headers.set('Accept', 'image/*,*/*;q=0.8');
  let hadToken = false;
  if (getToken) {
    const token = await getToken();
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
      hadToken = true;
    }
  }

  const response = await fetch(`${apiBaseUrl}${path}`, { ...init, headers });

  if (!response.ok) {
    // Same global 401 bounce as apiFetch: only when the request actually
    // carried a token (so an anonymous 401 doesn't loop us through /login)
    // and we're not already sitting on the login screen.
    if (response.status === 401 && hadToken && typeof window !== 'undefined') {
      try { window.sessionStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* sessionStorage may be blocked */ }
      if (!window.location.pathname.startsWith('/login')) {
        window.location.replace('/login?reason=expired');
      }
    }
    const method = (init.method ?? 'GET').toUpperCase();
    const status = `HTTP ${response.status}${response.statusText ? ' ' + response.statusText : ''}`;
    const err: ApiError = {
      status: response.status,
      message: `${method} ${path}: ${status}`,
    };
    throw err;
  }

  return await response.blob();
}

function safeJson(text: string): unknown {
  try { return JSON.parse(text); } catch { return text; }
}
